import CustomDrawer from "@/components/drawer";
import useIsSmall from "@/hooks/useIsSmall";
import { faNumber } from "@/modules/mobileHome/format";
import { useSmartAudienceCustomers } from "@/services/smartAudience/hooks";
import { Alert, Button, Checkbox, Group, Modal, ScrollArea, Skeleton, Stack, Text } from "@mantine/core";
import { ReactNode } from "react";

type Props = {
  opened: boolean;
  onClose: () => void;
  audienceKey: string;
  merchantUserId?: number;
  excluded: number[];
  onExcludedChange: (ids: number[]) => void;
};

const visitDay = (value?: string | null) =>
  value ? new Intl.DateTimeFormat("fa-IR", { day: "numeric", month: "long" }).format(new Date(value)) : undefined;

// «دیدن افراد»: who a smart audience will reach, with a tick to leave someone out of this send.
export default function AudienceCustomers({ opened, onClose, audienceKey, merchantUserId, excluded, onExcludedChange }: Props) {
  const isSmall = useIsSmall();
  const query = useSmartAudienceCustomers(audienceKey, merchantUserId, opened);
  const rows = query.data?.pages.flatMap((page) => page.result ?? []) ?? [];
  const total = query.data?.pages[0]?.count ?? 0;
  const toggle = (id: number, included: boolean) =>
    onExcludedChange(included ? excluded.filter((item) => item !== id) : [...excluded, id]);

  const body: ReactNode = (
    <Stack gap="sm">
      <Text size="sm" c="dimmed">
        {query.isSuccess
          ? `${faNumber(total - excluded.length)} نفر از ${faNumber(total)} نفر پیامک می‌گیرند. تیک هر کس را بردارید، این بار برایش ارسال نمی‌شود.`
          : "فهرست افرادی که این پیامک را می‌گیرند."}
      </Text>
      <ScrollArea.Autosize mah={isSmall ? "55svh" : 420}>
        <Stack gap={4}>
          {query.isLoading
            ? [0, 1, 2].map((row) => <Skeleton key={row} height={52} radius="md" />)
            : query.isError
              ? (
                <Alert color="red">
                  فهرست دریافت نشد.
                  <Button variant="subtle" onClick={() => void query.refetch()}>
                    تلاش دوباره
                  </Button>
                </Alert>
              )
              : rows.map((row) => {
                const id = row.customerId ?? 0;
                const detail = [row.serviceTitle, visitDay(row.lastVisitDate) && `آخرین بار ${visitDay(row.lastVisitDate)}`, row.merchantUserName]
                  .filter(Boolean)
                  .join(" · ");
                return (
                  <Checkbox
                    key={id}
                    color="violet"
                    py={8}
                    checked={!excluded.includes(id)}
                    onChange={(event) => toggle(id, event.currentTarget.checked)}
                    label={row.fullName?.trim() || row.phoneNumber}
                    description={detail || undefined}
                  />
                );
              })}
          {query.hasNextPage && (
            <Button variant="subtle" color="violet" loading={query.isFetchingNextPage} onClick={() => void query.fetchNextPage()}>
              نمایش بیشتر
            </Button>
          )}
        </Stack>
      </ScrollArea.Autosize>
      <Group grow>
        <Button color="violet" onClick={onClose}>
          تأیید
        </Button>
      </Group>
    </Stack>
  );

  return isSmall ? (
    <CustomDrawer
      opened={opened}
      onClose={onClose}
      close={onClose}
      position="bottom"
      custom="audience-people"
      radius="md"
      // Mantine has no "auto" drawer size; the sheet is only as tall as its list.
      styles={{ content: { height: "auto", maxHeight: "calc(100% - 40px)" } }}
      title={<Text fw={800}>افرادی که پیامک می‌گیرند</Text>}
    >
      {body}
    </CustomDrawer>
  ) : (
    <Modal opened={opened} onClose={onClose} centered radius={20} title={<Text fw={800}>افرادی که پیامک می‌گیرند</Text>}>
      {body}
    </Modal>
  );
}

